import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import {
    ChevronLeft,
    ExternalLink,
    ShieldAlert,
    Copy,
    Check,
    Lock
} from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
    getUserById,
    getUserBookings,
    getPaymentAccounts,
    getUserRetreats,
    updateUserStatus
} from '../../Services/Users/UserApi';

// Safely decode Base64 encoded user ID or fallback to raw value
const decodeUserId = (encodedVal) => {
    if (!encodedVal) return '';
    try {
        const decoded = atob(decodeURIComponent(String(encodedVal)).trim());
        if (decoded && /^[\w\-@.]+$/.test(decoded)) {
            return decoded;
        }
        return encodedVal;
    } catch (e) {
        return encodedVal;
    }
};

// Safely encode user ID for URL query parameters
const encodeUserId = (rawVal) => {
    if (!rawVal) return '';
    try {
        return btoa(String(rawVal).trim());
    } catch (e) {
        return String(rawVal);
    }
};

const UserDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: paramId } = useParams();

    const rawUserId = paramId || location.state?.userId || location.state?.user?.id;
    const userId = decodeUserId(rawUserId);

    // States - populated from API call
    const [user, setUser] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [retreats, setRetreats] = useState([]);

    const [loadingUser, setLoadingUser] = useState(true);
    const [loadingBookings, setLoadingBookings] = useState(true);
    const [loadingAccounts, setLoadingAccounts] = useState(true);
    const [loadingRetreats, setLoadingRetreats] = useState(true);

    const [error, setError] = useState(null);
    const [copiedCode, setCopiedCode] = useState(false);

    // Selected payment account modal
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [openAccountModal, setOpenAccountModal] = useState(false);

    // Suspend modal state
    const [openSuspendModal, setOpenSuspendModal] = useState(false);
    const [isSuspended, setIsSuspended] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Snackbar state
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    // Format helper (unified date & time formatting)
    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            ...(includeTime && {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            })
        });
    };

    const formatDatesRange = (checkIn, checkOut, fallbackDate) => {
        if (checkIn && checkOut) {
            const inDate = new Date(checkIn);
            const outDate = new Date(checkOut);
            if (!isNaN(inDate.getTime()) && !isNaN(outDate.getTime())) {
                const inDay = inDate.getDate();
                const outDay = outDate.getDate();
                const outMonth = outDate.toLocaleDateString('en-IN', { month: 'short' });
                return `${inDay}–${outDay} ${outMonth}`;
            }
        }
        return formatDate(fallbackDate);
    };

    // Fetch all user-related data
    const fetchAllData = useCallback(async () => {
        if (!userId) {
            setError("No user ID specified.");
            setLoadingUser(false);
            setLoadingBookings(false);
            setLoadingAccounts(false);
            setLoadingRetreats(false);
            return;
        }

        try {
            // 1. Fetch User details directly from backend API
            setLoadingUser(true);
            const userRes = await getUserById(userId);
            if (userRes && (userRes.data || userRes.user)) {
                const userData = userRes.data || userRes.user;
                setUser(userData);
                setIsSuspended(userData.status === 'suspended');
            } else {
                setError("User details not found.");
            }
        } catch (err) {
            console.error('Error fetching user info:', err);
            setError("Could not load user profile details.");
        } finally {
            setLoadingUser(false);
        }

        // 2. Fetch Bookings
        try {
            setLoadingBookings(true);
            const bookingsRes = await getUserBookings(userId, 1, 50);
            if (bookingsRes && bookingsRes.success) {
                setBookings(bookingsRes.data || []);
            } else {
                setBookings([]);
            }
        } catch (err) {
            console.error('Error fetching bookings:', err);
            setBookings([]);
        } finally {
            setLoadingBookings(false);
        }

        // 3. Fetch Payment Accounts
        try {
            setLoadingAccounts(true);
            const accountsRes = await getPaymentAccounts(userId);
            if (accountsRes && accountsRes.success) {
                setPaymentAccounts(accountsRes.data || []);
            } else {
                setPaymentAccounts([]);
            }
        } catch (err) {
            console.error('Error fetching payment accounts:', err);
            setPaymentAccounts([]);
        } finally {
            setLoadingAccounts(false);
        }

        // 4. Fetch Retreats
        try {
            setLoadingRetreats(true);
            const retreatsRes = await getUserRetreats(userId, 1, 50);
            if (retreatsRes && retreatsRes.success) {
                setRetreats(retreatsRes.data || []);
            } else {
                setRetreats([]);
            }
        } catch (err) {
            console.error('Error fetching retreats:', err);
            setRetreats([]);
        } finally {
            setLoadingRetreats(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Computed Stats
    const totalLifetimeValue = useMemo(() => {
        return bookings.reduce((sum, b) => {
            const val = Number(b.amountPaid || b.totalAmount || b.payableAmount || 0);
            return sum + (isNaN(val) ? 0 : val);
        }, 0);
    }, [bookings]);

    const openRefundsCount = useMemo(() => {
        return bookings.filter(b => {
            const status = (b.bookingStatus || b.paymentStatus || '').toLowerCase();
            return status.includes('refund');
        }).length;
    }, [bookings]);

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount || amount === 0) return '₹0';
        if (amount >= 100000) {
            return `₹${(amount / 100000).toFixed(1)}L`;
        }
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    // Copy affiliate code
    const handleCopyAffiliateCode = () => {
        if (user?.affiliateCode) {
            navigator.clipboard.writeText(user.affiliateCode);
            setCopiedCode(true);
            setSnackbarMessage('Affiliate code copied to clipboard');
            setOpenSnackbar(true);
            setTimeout(() => setCopiedCode(false), 2000);
        }
    };


    const handleToggleSuspend = async () => {
        if (!userId) return;
        const targetStatus = isSuspended ? 'active' : 'suspended';
        try {
            setUpdatingStatus(true);
            const res = await updateUserStatus(userId, targetStatus);
            if (res && res.success) {
                const newSuspended = targetStatus === 'suspended';
                setIsSuspended(newSuspended);
                setUser(prev => prev ? { ...prev, status: targetStatus } : prev);
                setSnackbarMessage(res.message || (newSuspended ? 'User account suspended successfully' : 'User account activated successfully'));
                setOpenSnackbar(true);
                setOpenSuspendModal(false);

                // Refresh latest user details to update audit fields (uuName, uuType, uuId, updatedAt)
                try {
                    const refreshed = await getUserById(userId);
                    if (refreshed && (refreshed.data || refreshed.user)) {
                        const updatedData = refreshed.data || refreshed.user;
                        setUser(updatedData);
                        setIsSuspended(updatedData.status === 'suspended');
                    }
                } catch (e) {
                    console.error("Error refreshing user after status update:", e);
                }
            } else {
                setSnackbarMessage(res?.message || 'Failed to update user status');
                setOpenSnackbar(true);
            }
        } catch (error) {
            console.error('Error toggling suspend status:', error);
            const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update user status';
            setSnackbarMessage(errorMsg);
            setOpenSnackbar(true);
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Status chip helper
    const getBookingStateChip = (status) => {
        const s = (status || '').toLowerCase();
        let bg = '#F1F5F9';
        let color = '#475569';
        let label = status || 'Pending';

        if (s.includes('completed') || s.includes('confirmed') || s.includes('success')) {
            bg = '#E8F5E8';
            color = '#2E7D32';
            label = 'completed';
        } else if (s.includes('refund under review') || s.includes('refund requested') || s.includes('refund')) {
            bg = '#FFF1F2';
            color = '#BE123C';
            label = 'refund under review';
        } else if (s.includes('transfer requested') || s.includes('request')) {
            bg = '#EFF6FF';
            color = '#1D4ED8';
            label = 'transfer requested';
        } else if (s.includes('cancelled')) {
            bg = '#FEE2E2';
            color = '#B91C1C';
            label = 'cancelled';
        }

        return (
            <Box
                sx={{
                    px: 1.2,
                    py: 0.35,
                    borderRadius: '4px',
                    bgcolor: bg,
                    color: color,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    display: 'inline-block',
                    textTransform: 'lowercase',
                    border: '1px solid transparent'
                }}
            >
                {label}
            </Box>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#F8FAFC', minHeight: '100vh', color: '#1E293B' }}>
            {/* Top Navigation Bar */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <IconButton
                        disableFocusRipple disableRipple disableTouchRipple
                        onClick={() => navigate('/user')}
                        sx={{
                            color: '#1E293B',
                            bgcolor: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '6px',
                            p: 0.75,
                            '&:hover': { bgcolor: '#F1F5F9' }
                        }}
                    >
                        <ChevronLeft size={18} />
                    </IconButton>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1.25rem', md: '1.4rem' },
                                color: '#1E293B'
                            }}
                        >
                            User Profile
                        </Typography>
                    </Box>
                </Stack>

                <Button
                    variant="outlined"
                    onClick={() => navigate('/user')}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: '#64748B',
                        borderColor: '#E2E8F0',
                        borderRadius: '6px',
                        bgcolor: '#FFFFFF',
                        px: 2,
                        py: 0.6,
                        '&:hover': {
                            bgcolor: '#F8FAFC',
                            borderColor: '#CBD5E1'
                        }
                    }}
                >
                    Back to Users
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '6px', fontSize: '0.85rem' }}>
                    {error}
                </Alert>
            )}

            {/* Main Content Grid */}
            <Grid container spacing={2.5}>
                {/* Left Column: User Profile, Bookings, Payment accounts, Retreats */}
                <Grid item size={{ xs: 12, lg: 9 }}>
                    <Stack spacing={2.5}>
                        {/* 1. User Info Header Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, md: 3 },
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            {loadingUser ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                </Box>
                            ) : (
                                <Box>
                                    {/* Name & Roles Header */}
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: '1.15rem', md: '1.3rem' },
                                                color: '#1E293B'
                                            }}
                                        >
                                            {user?.name || 'Unnamed User'}
                                        </Typography>

                                        {/* Role Pills */}
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: '4px',
                                                bgcolor: '#EFF6FF',
                                                color: '#1D4ED8',
                                                border: '1px solid #DBEAFE',
                                                fontSize: '0.72rem',
                                                fontWeight: 500
                                            }}
                                        >
                                            Guest
                                        </Box>
                                        {user?.isAffiliate && (
                                            <Box
                                                sx={{
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: '4px',
                                                    bgcolor: '#FFF7ED',
                                                    color: '#C2410C',
                                                    border: '1px solid #FFEDD5',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500
                                                }}
                                            >
                                                Affiliate
                                            </Box>
                                        )}
                                        {user?.isHost && (
                                            <Box
                                                sx={{
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: '4px',
                                                    bgcolor: '#E8F5E8',
                                                    color: '#2E7D32',
                                                    border: '1px solid #C8E6C9',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500
                                                }}
                                            >
                                                Host
                                            </Box>
                                        )}

                                    </Stack>

                                    {/* User Details Key-Value List */}
                                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                                        {/* Email */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Email (login)
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 500, wordBreak: 'break-all' }}>
                                                {user?.email || 'N/A'}
                                            </Typography>
                                        </Stack>

                                        {/* Phone */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Phone
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 500 }}>
                                                {user?.phone || 'N/A'}
                                            </Typography>
                                        </Stack>

                                        {/* Sign-in provider */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Sign-in provider
                                            </Typography>
                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 500, textTransform: 'capitalize' }}>
                                                    {user?.provider || 'local'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>·</Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: user?.isEmailVerified ? '#2E7D32' : '#D97706', fontWeight: 500 }}>
                                                    {user?.isEmailVerified ? 'email verified' : 'unverified'}
                                                </Typography>
                                            </Stack>
                                        </Stack>

                                        {/* Terms agreed */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Terms agreed
                                            </Typography>
                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                <Box
                                                    sx={{
                                                        px: 0.8,
                                                        py: 0.2,
                                                        borderRadius: '3px',
                                                        bgcolor: user?.isAgreed ? '#E8F5E8' : '#FEE2E2',
                                                        color: user?.isAgreed ? '#2E7D32' : '#B91C1C',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {user?.isAgreed ? 'yes' : 'no'}
                                                </Box>
                                                <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
                                                    · {formatDate(user?.createdAt)}
                                                </Typography>
                                            </Stack>
                                        </Stack>

                                        {/* Joined */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Joined
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 500 }}>
                                                {formatDate(user?.createdAt)}
                                            </Typography>
                                        </Stack>

                                        {/* Last active */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Last active
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 500 }}>
                                                {formatDate(user?.updatedAt || user?.createdAt)}
                                            </Typography>
                                        </Stack>

                                        {/* Country */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Country
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', fontWeight: 500 }}>
                                                {user?.country || 'India'}
                                            </Typography>
                                        </Stack>

                                        {/* Status */}
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Typography sx={{ width: { xs: 120, sm: 140 }, flexShrink: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                                Status
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 0.9,
                                                    py: 0.2,
                                                    borderRadius: '4px',
                                                    bgcolor: isSuspended ? '#FEE2E2' : '#E8F5E8',
                                                    color: isSuspended ? '#B91C1C' : '#2E7D32',
                                                    border: `1px solid ${isSuspended ? '#FECACA' : '#C8E6C9'}`,
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize'
                                                }}
                                            >
                                                {isSuspended ? 'suspended' : 'active'}
                                            </Box>
                                        </Stack>
                                    </Stack>

                                </Box>
                            )}
                        </Paper>

                        {/* 2. Bookings Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ p: 2, px: 2.5, borderBottom: '1px solid #EEF2F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1E293B' }}>
                                    Bookings
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        const targetUserId = user?.id || userId;
                                        if (!targetUserId) return;
                                        navigate(`/bookings?userId=${encodeURIComponent(encodeUserId(targetUserId))}`);
                                    }}
                                    endIcon={<ExternalLink size={13} />}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.78rem',
                                        fontWeight: 500,
                                        color: '#803927',
                                        p: 0,
                                        minWidth: 'auto',
                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                    }}
                                >
                                    Open in Bookings
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #EEF2F6' }}>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>ID</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>Retreat</TableCell>

                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>Value</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>State</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>Booking Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loadingBookings ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                    <CircularProgress size={20} sx={{ color: '#FFBD65' }} />
                                                </TableCell>
                                            </TableRow>
                                        ) : bookings.length > 0 ? (
                                            bookings.slice(0, 5).map((booking) => (
                                                <TableRow
                                                    key={booking.id}
                                                    sx={{
                                                        '& td': { borderBottom: '1px solid #F1F5F9' },
                                                        '&:last-child td': { borderBottom: 0 }
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8rem', color: '#1E293B', fontWeight: 500 }}>
                                                        <Box
                                                            component="span"
                                                            onClick={() => {
                                                                const queryParams = new URLSearchParams();
                                                                const targetUserId = user?.id || userId;
                                                                if (targetUserId) queryParams.set('userId', encodeUserId(targetUserId));
                                                                if (booking.bookingId) queryParams.set('bookingId', booking.bookingId);
                                                                navigate(`/bookings?${queryParams.toString()}`);
                                                            }}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                color: '#803927',
                                                                fontWeight: 600,
                                                                '&:hover': { textDecoration: 'underline' }
                                                            }}
                                                        >
                                                            #{booking.bookingId}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8rem', color: '#1E293B', fontWeight: 500 }}>
                                                        {booking.retreat?.title || booking.retreatListing?.title || 'Retreat Booking'}
                                                    </TableCell>

                                                    <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8rem', color: '#1E293B', fontWeight: 500 }}>
                                                        ₹{Number(booking.totalAmount || 0).toLocaleString('en-IN')}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, px: 2 }}>
                                                        {getBookingStateChip(booking.bookingStatus)}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.75rem', color: '#64748B' }}>
                                                        {formatDatesRange(booking.checkInDate, booking.checkOutDate, booking.createdAt)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94A3B8', fontSize: '0.8rem' }}>
                                                    No bookings found for this user.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* 3. Payment Accounts Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ p: 2, px: 2.5, borderBottom: '1px solid #EEF2F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1E293B' }}>
                                        Payment accounts
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.72rem' }}>
                                        Used for affiliate payouts — never for charging the guest
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        const searchVal = user?.name || user?.email || '';
                                        navigate(`/payment-accounts?search=${encodeURIComponent(searchVal)}`, {
                                            state: {
                                                search: searchVal,
                                                name: user?.name,
                                                userName: user?.name,
                                                email: user?.email,
                                                userEmail: user?.email,
                                                userId: user?.id || userId
                                            }
                                        });
                                    }}
                                    endIcon={<ExternalLink size={13} />}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.78rem',
                                        fontWeight: 500,
                                        color: '#803927',
                                        p: 0,
                                        minWidth: 'auto',
                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                    }}
                                >
                                    View Payment Accounts
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #EEF2F6' }}>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>Type</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>Details</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }}>Default</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5, px: 2 }} align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loadingAccounts ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                    <CircularProgress size={20} sx={{ color: '#FFBD65' }} />
                                                </TableCell>
                                            </TableRow>
                                        ) : paymentAccounts.length > 0 ? (
                                            paymentAccounts.map((account, idx) => {
                                                const isDefault = account.isDefault || idx === 0;
                                                const verified = account.isVerified;
                                                const isUpi = (account.methodType === 'paypal' || "-");
                                                const maskedNumber = account.accountNumber ? `****${account.accountNumber.slice(-4)}` : '';
                                                const detailsText = isUpi
                                                    ? account.emailOfAccountHolder
                                                    : `${account.bankName || 'Bank'} ${maskedNumber} - ${account.accountHolderName || ''}`;

                                                return (
                                                    <TableRow
                                                        key={account.id || idx}
                                                        sx={{
                                                            '& td': { borderBottom: '1px solid #F1F5F9' },
                                                            '&:last-child td': { borderBottom: 0 }
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8rem', color: '#1E293B', fontWeight: 500 }}>
                                                            {(account.methodType || '-')}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8rem', color: '#475569' }}>
                                                            {detailsText}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                                            <Box
                                                                sx={{
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    borderRadius: '4px',
                                                                    // bgcolor: '#E8F5E8',
                                                                    color: verified === true ? '#2E7D32' : '#FF6B35',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 500,
                                                                    display: 'inline-block'
                                                                }}
                                                            >
                                                                {verified === true ? 'Verified' : 'Not Verified'}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.78rem', color: isDefault ? '#1E293B' : '#94A3B8' }}>
                                                            {isDefault ? 'Yes' : 'No'}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.5, px: 2 }} align="right">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    setSelectedAccount(account);
                                                                    setOpenAccountModal(true);
                                                                }}
                                                                sx={{
                                                                    fontSize: '0.72rem',
                                                                    textTransform: 'none',
                                                                    py: 0.2,
                                                                    px: 1.2,
                                                                    minWidth: 'auto',
                                                                    borderColor: '#E2E8F0',
                                                                    color: '#475569',
                                                                    borderRadius: '4px',
                                                                    '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' }
                                                                }}
                                                            >
                                                                View
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94A3B8', fontSize: '0.8rem' }}>
                                                    No payment accounts linked to this user.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* 4. Retreats Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ p: 2, px: 2.5, borderBottom: '1px solid #EEF2F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1E293B' }}>
                                    Retreats
                                </Typography>
                                {user?.isHost && (
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/user-retreats', { state: { userId: user?.id || userId, userName: user?.name } })}
                                        endIcon={<ExternalLink size={13} />}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.78rem',
                                            fontWeight: 500,
                                            color: '#803927',
                                            p: 0,
                                            minWidth: 'auto',
                                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                        }}
                                    >
                                        Open in Retreats
                                    </Button>
                                )}
                            </Box>

                            <Box sx={{ p: 2.5 }}>
                                {loadingRetreats ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={20} sx={{ color: '#FFBD65' }} />
                                    </Box>
                                ) : retreats.length > 0 ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #EEF2F6' }}>
                                                    <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', py: 1.5, px: 2 }}>ID</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', py: 1.5, px: 2 }}>Title</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', py: 1.5, px: 2 }}>Location</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.7rem', textTransform: 'uppercase', py: 1.5, px: 2 }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {retreats.slice(0, 5).map((retreat) => (
                                                    <TableRow key={retreat.id} sx={{ '& td': { borderBottom: '1px solid #F1F5F9' }, '&:last-child td': { borderBottom: 0 } }}>
                                                        <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8rem', color: '#1E293B', fontWeight: 500 }}>#{retreat.id}</TableCell>
                                                        <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8rem', color: '#1E293B', fontWeight: 500 }}>{retreat.title || 'Untitled Retreat'}</TableCell>
                                                        <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.75rem', color: '#64748B' }}>{retreat.location || retreat.city || '-'}</TableCell>
                                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                                            <Box sx={{ px: 1, py: 0.2, borderRadius: '4px', bgcolor: '#E8F5E8', color: '#2E7D32', fontSize: '0.7rem', fontWeight: 500, display: 'inline-block' }}>
                                                                {retreat.status || 'Active'}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
                                        {user?.isHost
                                            ? 'No retreats created yet by this host.'
                                            : 'This user is not a host — no listings. If their host application is approved, their listings appear here and on the Hosts screen.'}
                                    </Typography>
                                )}
                            </Box>
                        </Paper>
                    </Stack>
                </Grid>

                {/* Right Column: At a glance, Affiliate, Actions */}
                <Grid item size={{ xs: 12, lg: 3 }}>
                    <Stack spacing={2.5}>
                        {/* 1. At a glance Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1E293B', mb: 2 }}>
                                At a glance
                            </Typography>

                            <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B' }}>
                                        Bookings
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B' }}>
                                        {user?.bookings !== undefined ? user.bookings : (user?.atAGlance?.bookings !== undefined ? user.atAGlance.bookings : bookings.length)}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B' }}>
                                        Lifetime value
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B' }}>
                                        {user?.lifetimeValue !== undefined
                                            ? formatCurrency(user.lifetimeValue)
                                            : (user?.atAGlance?.lifetimeValue !== undefined
                                                ? formatCurrency(user.atAGlance.lifetimeValue)
                                                : formatCurrency(totalLifetimeValue))}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B' }}>
                                        Open refunds
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: '0.88rem',
                                        fontWeight: 600,
                                        color: ((user?.openRefunds ?? user?.atAGlance?.openRefunds ?? openRefundsCount) > 0) ? '#BE123C' : '#1E293B'
                                    }}>
                                        {user?.openRefunds !== undefined ? user.openRefunds : (user?.atAGlance?.openRefunds !== undefined ? user.atAGlance.openRefunds : openRefundsCount)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Paper>

                        {/* 2. Affiliate Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1E293B' }}>
                                    Affiliate
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => navigate('/affiliates')}
                                    endIcon={<ExternalLink size={13} />}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: '#803927',
                                        p: 0,
                                        minWidth: 'auto',
                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                    }}
                                >
                                    Ledger
                                </Button>
                            </Box>

                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.3 }}>
                                        Code
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Typography
                                            sx={{
                                                fontSize: '0.78rem',
                                                color: '#1E293B',
                                                bgcolor: '#F8FAFC',
                                                px: 1,
                                                py: 0.3,
                                                borderRadius: '4px',
                                                border: '1px solid #E2E8F0',
                                                fontFamily: 'monospace',
                                                flex: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={user?.affiliateCode}
                                        >
                                            {user?.affiliateCode || 'N/A'}
                                        </Typography>
                                        {user?.affiliateCode && (
                                            <IconButton
                                                size="small"
                                                onClick={handleCopyAffiliateCode}
                                                sx={{ p: 0.5, color: copiedCode ? '#2E7D32' : '#64748B' }}
                                                title="Copy affiliate code"
                                            >
                                                {copiedCode ? <Check size={14} /> : <Copy size={14} />}
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Box>

                                {/* <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B' }}>
                                        Attributed bookings
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B' }}>
                                        {bookings.length > 0 ? '1' : '0'}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B' }}>
                                        Earnings
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B' }}>
                                        ₹{Number(user?.totalEarning || 0).toLocaleString('en-IN')}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B' }}>
                                        Status
                                    </Typography>
                                    <Box
                                        sx={{
                                            px: 1,
                                            py: 0.2,
                                            borderRadius: '4px',
                                            bgcolor: user?.isAffiliate ? '#E8F5E8' : '#F1F5F9',
                                            color: user?.isAffiliate ? '#2E7D32' : '#64748B',
                                            fontSize: '0.72rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        {user?.isAffiliate ? 'active' : 'inactive'}
                                    </Box>
                                </Stack> */}
                            </Stack>
                        </Paper>

                        {/* 3. Actions Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1E293B', mb: 2 }}>
                                Actions
                            </Typography>

                            <Stack spacing={1.25}>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setOpenSuspendModal(true)}
                                    disabled={updatingStatus}
                                    startIcon={updatingStatus ? <CircularProgress size={14} color="inherit" /> : <ShieldAlert size={15} />}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.82rem',
                                        fontWeight: 500,
                                        color: isSuspended ? '#2E7D32' : '#C62828',
                                        borderColor: isSuspended ? '#A5D6A7' : '#FFCDD2',
                                        borderRadius: '6px',
                                        py: 0.8,
                                        bgcolor: '#FFFFFF',
                                        justifyContent: 'center',
                                        '&:hover': {
                                            bgcolor: isSuspended ? '#F1F8E9' : '#FFF5F5',
                                            borderColor: isSuspended ? '#81C784' : '#EF9A9A'
                                        }
                                    }}
                                >
                                    {updatingStatus ? 'Updating status...' : (isSuspended ? 'Activate account' : 'Suspend account')}
                                </Button>
                            </Stack>

                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mt: 2,
                                    color: '#94A3B8',
                                    fontSize: '0.72rem',
                                    lineHeight: 1.4
                                }}
                            >
                                Accounts are suspended, never deleted — bookings, invoices and the affiliate ledger must survive for audit.
                            </Typography>
                        </Paper>

                        {/* 4. Audit Information Card (Non-editable) */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: '8px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Lock size={14} color="#64748B" />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1E293B' }}>
                                        Audit Details
                                    </Typography>
                                </Stack>
                                <Box
                                    sx={{
                                        px: 0.8,
                                        py: 0.2,
                                        borderRadius: '3px',
                                        bgcolor: '#F1F5F9',
                                        border: '1px solid #E2E8F0',
                                        color: '#64748B',
                                        fontSize: '0.68rem',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Not Editable
                                </Box>
                            </Box>

                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 0.2 }}>
                                        Created By
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                        {user?.cuName || 'System Admin'} ({user?.cuType || 'admin'})
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 0.2 }}>
                                        Joined Date
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                        {formatDate(user?.createdAt, true)}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 0.2 }}>
                                        Updated By
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                        {user?.uuName || 'System Admin'} ({user?.uuType || 'admin'})
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 0.2 }}>
                                        Updated At
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                        {formatDate(user?.updatedAt, true)}
                                    </Typography>
                                </Box>

                                {user?.deletedAt && (
                                    <Box>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#C62828', mb: 0.2 }}>
                                            Deleted At
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#C62828' }}>
                                            {formatDate(user.deletedAt, true)}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>

            {/* View Payment Account Details Modal */}
            <Dialog
                open={openAccountModal}
                onClose={() => setOpenAccountModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        boxShadow: 'none',
                        bgcolor: '#FFFFFF'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: '#1E293B', borderBottom: '1px solid #EEF2F6', pb: 1.5 }}>
                    Payment Account Details
                </DialogTitle>
                <DialogContent sx={{ p: 2.5 }}>
                    {selectedAccount && (
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Method Type</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                    {selectedAccount.methodType || 'Bank'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Account Holder Name</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                    {selectedAccount.accountHolderName || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Bank Name</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                    {selectedAccount.bankName || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Account Number</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                    {selectedAccount.accountNumber || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>IBAN / IFSC</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                    {selectedAccount.iban || selectedAccount.ifsc || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Account Currency</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                    {selectedAccount.accountCurrency || 'INR'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Created Date</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                    {formatDate(selectedAccount.createdAt)}
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #EEF2F6' }}>
                    <Button
                        size="small"
                        onClick={() => setOpenAccountModal(false)}
                        sx={{ textTransform: 'none', color: '#64748B', fontSize: '0.82rem' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Suspend Account Confirmation Modal */}
            <Dialog
                open={openSuspendModal}
                onClose={() => !updatingStatus && setOpenSuspendModal(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        boxShadow: 'none',
                        bgcolor: '#FFFFFF'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: '#1E293B', pb: 1 }}>
                    {isSuspended ? 'Activate Account?' : 'Suspend Account?'}
                </DialogTitle>
                <DialogContent sx={{ pb: 2 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748B', lineHeight: 1.5 }}>
                        {isSuspended
                            ? 'Are you sure you want to reactivate this account? The user will regain access to their account.'
                            : 'Are you sure you want to suspend this user? Accounts are suspended and never deleted so that all past bookings and audit trails remain intact.'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <Button
                        size="small"
                        disabled={updatingStatus}
                        onClick={() => setOpenSuspendModal(false)}
                        sx={{ textTransform: 'none', color: '#64748B', fontSize: '0.82rem' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={updatingStatus}
                        onClick={handleToggleSuspend}
                        startIcon={updatingStatus ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : null}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.82rem',
                            fontWeight: 500,
                            bgcolor: isSuspended ? '#2E7D32' : '#C62828',
                            color: '#FFFFFF',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: isSuspended ? '#1B5E20' : '#B71C1C',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {updatingStatus ? 'Updating...' : (isSuspended ? 'Activate Account' : 'Suspend Account')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default UserDetails;
